import { z } from "zod";
import { isoDateTime, optionalText, text, uuid } from "./common";

/*
 * Documents (x-rays, photos, reports, scanned forms), medical image metadata and patient consent.
 * File bytes never pass through JSON: the API issues a short-lived signed upload URL, the browser PUTs
 * the bytes there, then calls /complete so the API can verify size and checksum before the file
 * becomes "available".
 */

export const DOCUMENT_CATEGORIES = ["xray", "photo", "lab_report", "consent", "prescription", "receipt", "treatment", "other"] as const;
export type DocumentCategory = (typeof DOCUMENT_CATEGORIES)[number];

export const DOCUMENT_STATUSES = ["pending_upload", "available", "quarantined", "deleted"] as const;
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];

export const IMAGE_MODALITIES = ["iopa", "opg", "cbct", "bitewing", "cephalometric", "intraoral_photo", "extraoral_photo", "other"] as const;
export type ImageModality = (typeof IMAGE_MODALITIES)[number];

/**
 * The only file types a clinic may upload, with the extension each is saved under. Anything that a
 * browser could execute (SVG, HTML, XML, JavaScript) is deliberately absent.
 */
export const ALLOWED_DOCUMENT_MIME_TYPES = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
  "application/pdf": [".pdf"],
  "application/dicom": [".dcm", ".dicom"],
} as const satisfies Record<string, readonly string[]>;
export type DocumentMimeType = keyof typeof ALLOWED_DOCUMENT_MIME_TYPES;
export const DOCUMENT_MIME_TYPES = Object.keys(ALLOWED_DOCUMENT_MIME_TYPES) as [DocumentMimeType, ...DocumentMimeType[]];
/** MIME types that can carry a medical image (DICOM or a picture). */
export const IMAGE_MIME_TYPES: readonly DocumentMimeType[] = ["image/jpeg", "image/png", "image/webp", "application/dicom"];

export const MAX_DOCUMENT_BYTES = 20 * 1024 * 1024;

/**
 * Makes a user-supplied filename safe to store and to offer back as a download name: no directories,
 * no control characters, no shell/HTML specials, bounded length, and an extension that matches the
 * declared MIME type (so "scan.html" uploaded as image/png comes back as "scan.html.png").
 */
export function sanitizeFilename(input: string, mimeType: DocumentMimeType): string {
  const base = input.split(/[\\/]/).pop() ?? "";
  let name = base
    .normalize("NFC")
    .replace(/[\p{Cc}\p{Cf}]/gu, "")
    .replace(/[^\p{L}\p{N}\p{M} ._()-]+/gu, "_")
    .replace(/\s+/g, " ")
    .replace(/^[.\s_]+/, "")
    .trim();
  const allowed: readonly string[] = ALLOWED_DOCUMENT_MIME_TYPES[mimeType];
  const lower = name.toLowerCase();
  if (!allowed.some((ext) => lower.endsWith(ext))) name = `${name || "file"}${allowed[0]}`;
  if (name.length > 150) {
    const ext = allowed.find((e) => name.toLowerCase().endsWith(e)) ?? allowed[0]!;
    name = `${name.slice(0, 150 - ext.length)}${ext}`;
  }
  return name;
}

/** FDI two-digit tooth numbers: permanent 11–48, primary 51–85. */
const toothCode = z.string().regex(/^([1-4][1-8]|[5-8][1-5])$/, "Use an FDI tooth number (11–48 permanent, 51–85 primary)");

/** "module.entity" reference, e.g. "clinical.visit" or "clinical.treatment_plan". */
const entityType = z.string().regex(/^[a-z_]+\.[a-z_]+$/, "Use module.entity, e.g. clinical.visit").max(64);

export const medicalImageInput = z.object({
  modality: z.enum(IMAGE_MODALITIES),
  toothCodes: z.array(toothCode).max(52).default([]),
  takenAt: isoDateTime.optional(),
  visitId: uuid.optional(),
  notes: optionalText(2000),
});

export const createDocumentInput = z
  .object({
    category: z.enum(DOCUMENT_CATEGORIES),
    filename: text(255),
    mimeType: z.string().trim().toLowerCase().pipe(z.enum(DOCUMENT_MIME_TYPES, { error: "This file type is not allowed. Upload JPEG, PNG, WebP, PDF or DICOM." })),
    sizeBytes: z.number().int().min(1).max(MAX_DOCUMENT_BYTES, "Files can be at most 20 MB"),
    relatedEntityType: entityType.optional(),
    relatedEntityId: uuid.optional(),
    image: medicalImageInput.optional(),
  })
  .refine((v) => (v.relatedEntityType === undefined) === (v.relatedEntityId === undefined), {
    message: "Give both relatedEntityType and relatedEntityId, or neither",
    path: ["relatedEntityId"],
  })
  .refine((v) => !v.image || IMAGE_MIME_TYPES.includes(v.mimeType), {
    message: "Medical images must be JPEG, PNG, WebP or DICOM",
    path: ["mimeType"],
  });
export type CreateDocumentInput = z.infer<typeof createDocumentInput>;

export const documentListQuery = z.object({
  category: z.enum(DOCUMENT_CATEGORIES).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().max(300).optional(),
});

export const documentDownloadQuery = z.object({
  inline: z
    .enum(["true", "false"])
    .optional()
    .transform((v) => v === "true"),
});

export const deleteDocumentInput = z.object({ reason: text(500) });
export const legalHoldInput = z.object({ on: z.boolean(), reason: text(500) });

export interface MedicalImage {
  id: string;
  modality: ImageModality;
  toothCodes: string[];
  takenAt: string | null;
  visitId: string | null;
  takenBy: string | null;
  notes: string | null;
}

export interface DocumentRecord {
  id: string;
  patientId: string | null;
  category: DocumentCategory;
  originalFilename: string | null;
  mimeType: DocumentMimeType;
  sizeBytes: number;
  sha256: string | null;
  status: DocumentStatus;
  legalHold: boolean;
  relatedEntityType: string | null;
  relatedEntityId: string | null;
  uploadedBy: string | null;
  uploadedAt: string | null;
  createdAt: string;
  deletedAt: string | null;
  image: MedicalImage | null;
}

export interface SignedUrlResponse {
  url: string;
  method: "PUT" | "GET";
  expiresAt: string;
}

export interface CreateDocumentResponse {
  document: DocumentRecord;
  /** PUT the bytes here with the same Content-Type, then call POST /api/documents/:id/complete. */
  upload: SignedUrlResponse;
}

// ---------------------------------------------------------------------------------------------------
// Consent
// ---------------------------------------------------------------------------------------------------

export const CONSENT_BODY_FORMATS = ["markdown", "text"] as const;
export const CONSENT_SIGNER_RELATIONSHIPS = ["self", "parent", "legal_guardian", "spouse", "child", "sibling", "other_relative", "other"] as const;
export const CONSENT_STATUSES = ["signed", "revoked"] as const;

/**
 * Deliberately empty. Consent wording is legal text that each clinic (and its advisers) must write
 * for its own procedures, language and jurisdiction; DentOS must not invent it or ship "default"
 * wording that a clinic might rely on without review. Clinics create their own templates and versions.
 */
export const STARTER_CONSENT_TEMPLATES: readonly never[] = [];

export const consentTemplateInput = z.object({
  code: z.string().trim().toLowerCase().regex(/^[a-z0-9_]{1,64}$/, "Use lowercase letters, digits and _"),
  name: text(200),
  description: optionalText(1000),
});

export const consentTemplateVersionInput = z.object({
  title: text(300),
  language: z.string().trim().regex(/^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/, "Use a language code like en or ml-IN").default("en"),
  body: z.string().trim().min(1).max(100_000),
  bodyFormat: z.enum(CONSENT_BODY_FORMATS).default("markdown"),
});

export const recordConsentInput = z
  .object({
    templateVersionId: uuid,
    signerName: text(200),
    signerRelationship: z.enum(CONSENT_SIGNER_RELATIONSHIPS).default("self"),
    signedAt: isoDateTime.optional(),
    witnessStaffId: uuid.optional(),
    signatureDocumentId: uuid.optional(),
    relatedEntityType: entityType.optional(),
    relatedEntityId: uuid.optional(),
  })
  .refine((v) => (v.relatedEntityType === undefined) === (v.relatedEntityId === undefined), {
    message: "Give both relatedEntityType and relatedEntityId, or neither",
    path: ["relatedEntityId"],
  });

export const revokeConsentInput = z.object({ reason: text(500) });

export interface ConsentTemplateVersion {
  id: string;
  templateId: string;
  versionNo: number;
  title: string;
  language: string;
  bodyFormat: (typeof CONSENT_BODY_FORMATS)[number];
  body: string;
  bodySha256: string;
  publishedAt: string;
  publishedBy: string | null;
}

export interface ConsentTemplate {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  latestVersionNo: number | null;
}

export interface ConsentTemplateDetail extends ConsentTemplate {
  versions: ConsentTemplateVersion[];
}

export interface PatientConsent {
  id: string;
  patientId: string;
  templateVersionId: string;
  templateId: string;
  templateName: string;
  versionNo: number;
  title: string;
  bodySha256: string;
  signedAt: string;
  signerName: string;
  signerRelationship: (typeof CONSENT_SIGNER_RELATIONSHIPS)[number];
  witnessStaffId: string | null;
  signatureDocumentId: string | null;
  relatedEntityType: string | null;
  relatedEntityId: string | null;
  status: (typeof CONSENT_STATUSES)[number];
  revokedAt: string | null;
  revokedReason: string | null;
  recordedBy: string | null;
  createdAt: string;
}

/** "Send to patient": a signed link to one x-ray or photo, valid for 1 to 7 days. */
export const shareDocumentInput = z.object({ days: z.number().int().min(1).max(7).default(3) });
export interface DocumentShareLink {
  url: string;
  expiresAt: string;
}
